/**
 * Picks the right name/description/category for the active i18n language.
 *
 * Tenants author the primary copy in `name` / `description` / `category`
 * (typically English) and the optional secondary-language copy in the matching
 * `_alt` columns (typically Arabic, but the schema is locale-agnostic). When the
 * active language is the primary (`en`), we render the primary columns. For any
 * other language we try `_alt` first and fall back to the primary if the tenant
 * hasn't authored the translation.
 *
 * Mirrors selectPackageCopy in ./format-package-label.ts.
 */
export function selectClassTypeCopy(
  classType: {
    name?: string | null;
    name_alt?: string | null;
    description?: string | null;
    description_alt?: string | null;
    category?: string | null;
    category_alt?: string | null;
  },
  language: string,
  primaryLanguage: string = 'en',
): { name: string | null; description: string | null; category: string | null } {
  const isAlt = language !== primaryLanguage;

  const pick = (primary?: string | null, alt?: string | null): string | null => {
    if (!isAlt) return primary ?? null;
    const trimmedAlt = (alt ?? '').trim();
    return trimmedAlt.length > 0 ? trimmedAlt : (primary ?? null);
  };

  return {
    name: pick(classType.name, classType.name_alt),
    description: pick(classType.description, classType.description_alt),
    category: pick(classType.category, classType.category_alt),
  };
}
